import { useCallback } from 'react';

export interface DeviceInfo {
  platform: string;
  platformVersion: string;
  platformArchitecture: string;
  browser: string;
  browserVersion: string;
  screenResolution: string;
  windowResolution: string;
  deviceInfo: string;
  deviceType: 'mobile' | 'likely mobile' | 'tablet' | 'likely tablet' | 'desktop';
  touchScreen: 'yes' | 'no' | 'notCertain';
}

// Augment Navigator to include the UA Client Hints API types
interface NavigatorUAData {
  platform: string;
  mobile: boolean;
  getHighEntropyValues(hints: string[]): Promise<Record<string, string>>;
}

declare global {
  interface Navigator {
    userAgentData?: NavigatorUAData;
  }
}

export const useDeviceCollector = () => {
  const collect = useCallback(async (): Promise<DeviceInfo> => {
    const ua = navigator.userAgent;
    let platform = 'Unknown';
    let platformVersion = 'Unknown';
    let platformArchitecture = 'Unknown';
    let deviceInfo = 'Unknown';

    if (navigator.userAgentData) {
      try {
        const hints = await navigator.userAgentData.getHighEntropyValues([
          'platformVersion',
          'architecture',
          'model',
        ]);

        platform = navigator.userAgentData.platform || 'Unknown';
        platformVersion = hints['platformVersion'] || 'Unknown';
        platformArchitecture = hints['architecture'] || 'Unknown';
        deviceInfo = hints['model'] || 'Unknown';

        // Distinguish Windows 10 vs Windows 11 via platformVersion major segment.
        // Windows 11 reports major version >= 13; Windows 10 reports < 13.
        // See: https://learn.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11
        if (platform === 'Windows') {
          const majorVer = parseInt(platformVersion.split('.')[0], 10);
          if (Number.isFinite(majorVer)) {
            platform = majorVer >= 13 ? 'Windows 11' : 'Windows 10';
          }
          // If majorVer is NaN (missing/unknown platformVersion), leave platform as "Windows"
        }
      } catch {
        // High-entropy hints unavailable — fall through to UA string parsing
      }
    }

    // UA string fallback (Safari, Firefox, and non-Chromium browsers)
    if (platform === 'Unknown') {
      if (/iPhone/i.test(ua)) {
        platform = 'iOS';
        const match = ua.match(/OS ([\d_]+)/);
        platformVersion = match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
      } else if (/iPad/i.test(ua) || (/Mac/i.test(ua) && navigator.maxTouchPoints > 1)) {
        platform = 'iPadOS';
        const match = ua.match(/OS ([\d_]+)/);
        platformVersion = match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
      } else if (/Android/i.test(ua)) {
        platform = 'Android';
        const match = ua.match(/Android ([\d.]+)/);
        platformVersion = match?.[1] ?? 'Unknown';
      } else if (/Windows NT/i.test(ua)) {
        // UA string cannot distinguish Win 10 vs Win 11
        platform = 'Windows (10 or 11)';
        const match = ua.match(/Windows NT ([\d.]+)/);
        platformVersion = match?.[1] ?? 'Unknown';
      } else if (/Mac OS X/i.test(ua)) {
        platform = 'macOS';
        const match = ua.match(/Mac OS X ([\d_]+)/);
        platformVersion = match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
      } else if (/Linux/i.test(ua)) {
        platform = 'Linux';
      }
    }

    // Architecture fallback from UA string
    if (platformArchitecture === 'Unknown') {
      if (/WOW64|Win64|x64|x86_64/.test(ua)) platformArchitecture = 'x64';
      else if (/arm64|aarch64/i.test(ua)) platformArchitecture = 'arm64';
      else if (/arm/i.test(ua)) platformArchitecture = 'arm';
      else if (/x86|i[356]86/i.test(ua)) platformArchitecture = 'x86';
    }

    const { browser, browserVersion } = getBrowserInfo(ua);
    const deviceType = getDeviceType();
    const touchScreen = typeof navigator.maxTouchPoints === 'number'
      ? navigator.maxTouchPoints > 0 ? 'yes' : 'no'
      : 'notCertain';

    return {
      platform,
      platformVersion,
      platformArchitecture,
      browser,
      browserVersion,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      windowResolution: `${window.innerWidth}x${window.innerHeight}`,
      deviceInfo,
      deviceType,
      touchScreen,
    };
  }, []);

  return { collect };
};

function getBrowserInfo(ua: string): { browser: string; browserVersion: string } {
  // Order matters: Edge (Edg/) must be checked before Chrome; Opera (OPR/) before Chrome
  const browsers: Array<{ name: string; regex: RegExp }> = [
    { name: 'Edge', regex: /Edg\/([\d.]+)/ },
    { name: 'Opera', regex: /OPR\/([\d.]+)/ },
    { name: 'Chrome', regex: /Chrome\/([\d.]+)/ },
    { name: 'Firefox', regex: /Firefox\/([\d.]+)/ },
    { name: 'Safari', regex: /Version\/([\d.]+).*Safari\// },
  ];

  for (const { name, regex } of browsers) {
    const match = ua.match(regex);
    if (match) return { browser: name, browserVersion: match[1] };
  }

  return { browser: 'Unknown', browserVersion: 'Unknown' };
}

function getDeviceType(): 'mobile' | 'likely mobile' | 'tablet' | 'likely tablet' | 'desktop' {
  const ua = navigator.userAgent;

  // Explicit mobile UA patterns
  if (/iPhone|Android.*Mobile|Mobile.*Android/i.test(ua)) return 'mobile';

  // Explicit tablet UA patterns
  if (/iPad|Android(?!.*Mobile)/i.test(ua)) return 'tablet';

  // iPadOS 13+ reports as macOS; detect via touch points
  if (/Mac/i.test(ua) && navigator.maxTouchPoints > 1) return 'tablet';

  // Generic touch device with a small screen is likely a mobile device
  if (navigator.maxTouchPoints > 0 && window.screen.width <= 767) return 'likely mobile';

  // Generic touch device with a medium screen is likely a tablet
  if (navigator.maxTouchPoints > 0 && window.screen.width <= 1024) return 'likely tablet';

  return 'desktop';
}
